// Iter 82.3 / Phase E1.3 — Team Calendar full-page view.
// Month grid (default) + Week swimlane toggle. Filters: department,
// reporting manager, leave type, status, location (future-ready).
// Backend uses /api/leave/team-calendar/flat with all filters composed.
import { useCallback, useEffect, useMemo, useState } from "react";
import { CaretLeft, CaretRight, FunnelSimple, Calendar, ListBullets } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { formatDate } from "../../lib/format";
import { Link } from "react-router-dom";

const LEAVE_COLORS = {
    CL: "bg-sky-100 text-sky-800 border-sky-200",
    SL: "bg-rose-100 text-rose-800 border-rose-200",
    EL: "bg-emerald-100 text-emerald-800 border-emerald-200",
    LOP: "bg-amber-100 text-amber-800 border-amber-200",
    DEFAULT: "bg-slate-100 text-slate-800 border-slate-200",
};

function pad(n) { return n < 10 ? `0${n}` : `${n}`; }
function isoDate(y, m, d) { return `${y}-${pad(m + 1)}-${pad(d)}`; }
function startOfMonth(date) { return new Date(date.getFullYear(), date.getMonth(), 1); }
function endOfMonth(date) { return new Date(date.getFullYear(), date.getMonth() + 1, 0); }

function eachDayInRange(start, end) {
    const days = [];
    const cur = new Date(start);
    while (cur <= end) {
        days.push(new Date(cur));
        cur.setDate(cur.getDate() + 1);
    }
    return days;
}

export default function TeamCalendar() {
    const [view, setView] = useState("month");
    const [anchor, setAnchor] = useState(new Date());
    const [filters, setFilters] = useState({
        department: "",
        reporting_manager_id: "",
        leave_type_code: "",
        status: "",
        location: "",
    });
    const [hrMeta, setHrMeta] = useState({ departments: [] });
    const [employees, setEmployees] = useState([]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    // Build the visible window
    const window = useMemo(() => {
        if (view === "month") {
            const start = startOfMonth(anchor);
            const end = endOfMonth(anchor);
            // Pad to full weeks (Mon-start)
            const padStart = new Date(start);
            const offset = (padStart.getDay() + 6) % 7;
            padStart.setDate(padStart.getDate() - offset);
            const padEnd = new Date(end);
            const tail = (7 - ((padEnd.getDay() + 6) % 7) - 1) % 7;
            padEnd.setDate(padEnd.getDate() + tail);
            return { start, end, padStart, padEnd };
        }
        // week — start on Monday of anchor
        const start = new Date(anchor);
        start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        return { start, end, padStart: start, padEnd: end };
    }, [view, anchor]);

    const loadMeta = useCallback(async () => {
        try {
            const [hr, emps] = await Promise.all([
                api.get("/hr/employee/meta"),
                api.get("/hr/employee/list", { params: { page_size: 500, status: "Active" } }),
            ]);
            setHrMeta(hr.data);
            setEmployees(emps.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load metadata"));
        }
    }, []);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const from = isoDate(window.padStart.getFullYear(), window.padStart.getMonth(), window.padStart.getDate());
            const to = isoDate(window.padEnd.getFullYear(), window.padEnd.getMonth(), window.padEnd.getDate());
            const params = { from_date: from, to_date: to };
            Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
            const { data } = await api.get("/leave/team-calendar/flat", { params });
            setItems(data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load calendar"));
        } finally {
            setLoading(false);
        }
    }, [window, filters]);

    useEffect(() => { Promise.resolve().then(loadMeta); }, [loadMeta]);
    useEffect(() => { reload(); }, [reload]);

    const itemsByDate = useMemo(() => {
        const map = {};
        items.forEach((req) => {
            const start = new Date(req.from_date);
            const end = new Date(req.to_date);
            eachDayInRange(start, end).forEach((d) => {
                const key = isoDate(d.getFullYear(), d.getMonth(), d.getDate());
                if (!map[key]) map[key] = [];
                map[key].push(req);
            });
        });
        return map;
    }, [items]);

    const move = (delta) => {
        const next = new Date(anchor);
        if (view === "month") next.setMonth(next.getMonth() + delta);
        else next.setDate(next.getDate() + delta * 7);
        setAnchor(next);
    };

    const title = view === "month"
        ? anchor.toLocaleDateString("en-IN", { month: "long", year: "numeric" })
        : `Week of ${formatDate(isoDate(window.start.getFullYear(), window.start.getMonth(), window.start.getDate()))}`;

    const visibleDays = eachDayInRange(window.padStart, window.padEnd);

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1600px]" data-testid="team-calendar-page">
            <PageHeader
                eyebrow="Leave Operations"
                title="Team Calendar"
                description="Organisation-wide leave overlay. Filter by department, manager, leave type, or status. Click a leave bar to open the request."
                actions={(
                    <div className="flex items-center gap-2">
                        <Button variant={view === "month" ? "default" : "outline"} size="sm"
                            onClick={() => setView("month")} data-testid="tc-view-month">
                            <Calendar size={14} className="mr-1" /> Month
                        </Button>
                        <Button variant={view === "week" ? "default" : "outline"} size="sm"
                            onClick={() => setView("week")} data-testid="tc-view-week">
                            <ListBullets size={14} className="mr-1" /> Week
                        </Button>
                    </div>
                )}
            />

            <section className="mt-5 bg-white rounded-xl border border-slate-200 p-4" data-testid="tc-filters">
                <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-700">
                    <FunnelSimple size={14} /> Filters
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div>
                        <label className="text-xs text-slate-600">Department</label>
                        <select
                            data-testid="tc-dept"
                            className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                            value={filters.department}
                            onChange={(e) => setFilters({ ...filters, department: e.target.value })}
                        >
                            <option value="">All departments</option>
                            {(hrMeta.departments || []).map((d) => (<option key={d.id || d.name} value={d.name}>{d.name}</option>))}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-slate-600">Reporting Manager</label>
                        <select
                            data-testid="tc-manager"
                            className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                            value={filters.reporting_manager_id}
                            onChange={(e) => setFilters({ ...filters, reporting_manager_id: e.target.value })}
                        >
                            <option value="">All managers</option>
                            {employees.map((e) => (<option key={e.id} value={e.id}>{e.employee_code} · {e.full_name}</option>))}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-slate-600">Leave Type</label>
                        <select
                            data-testid="tc-type"
                            className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                            value={filters.leave_type_code}
                            onChange={(e) => setFilters({ ...filters, leave_type_code: e.target.value })}
                        >
                            <option value="">All leave types</option>
                            {Array.from(new Set(items.map((r) => r.leave_type_code))).map((c) => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-slate-600">Status</label>
                        <select
                            data-testid="tc-status"
                            className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                            value={filters.status}
                            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                        >
                            <option value="">All statuses</option>
                            <option value="Pending">Pending</option>
                            <option value="Clarification Requested">Clarification Requested</option>
                            <option value="Approved">Approved</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-slate-600">Location (future)</label>
                        <input
                            type="text" data-testid="tc-location"
                            placeholder="e.g. Bangalore"
                            className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                            value={filters.location}
                            onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                        />
                    </div>
                </div>
            </section>

            <section className="mt-5 bg-white rounded-xl border border-slate-200 p-4" data-testid="tc-grid">
                <div className="flex items-center justify-between mb-3">
                    <Button variant="ghost" size="sm" onClick={() => move(-1)} data-testid="tc-prev">
                        <CaretLeft size={14} />
                    </Button>
                    <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
                    <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setAnchor(new Date())} data-testid="tc-today">Today</Button>
                        <Button variant="ghost" size="sm" onClick={() => move(1)} data-testid="tc-next">
                            <CaretRight size={14} />
                        </Button>
                    </div>
                </div>

                {loading && <div className="text-center py-12 text-slate-400">Loading…</div>}

                {!loading && view === "month" && (
                    <div className="grid grid-cols-7 gap-1">
                        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                            <div key={d} className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold py-1 px-2">{d}</div>
                        ))}
                        {visibleDays.map((d, i) => {
                            const key = isoDate(d.getFullYear(), d.getMonth(), d.getDate());
                            const inMonth = d.getMonth() === anchor.getMonth();
                            const isToday = key === isoDate(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
                            const dayItems = itemsByDate[key] || [];
                            return (
                                <div key={i}
                                    data-testid={`tc-day-${key}`}
                                    className={`min-h-[96px] p-1.5 border rounded-md text-xs
                                        ${inMonth ? "bg-white border-slate-200" : "bg-slate-50 border-slate-100 text-slate-400"}
                                        ${isToday ? "ring-2 ring-amber-400" : ""}`}>
                                    <div className={`font-mono text-[11px] ${inMonth ? "text-slate-700" : "text-slate-400"}`}>{d.getDate()}</div>
                                    <div className="mt-1 space-y-0.5">
                                        {dayItems.slice(0, 3).map((req) => (
                                            <Link key={`${req.id}-${key}`} to={`/leave/requests/${req.id}`}
                                                className={`block truncate px-1 py-0.5 rounded border text-[10px] hover:opacity-80
                                                    ${LEAVE_COLORS[req.leave_type_code] || LEAVE_COLORS.DEFAULT}`}
                                                data-testid={`tc-pill-${req.id}`}>
                                                <span className="font-semibold">{req.leave_type_code}</span> · {req.employee_name || req.employee_code}
                                            </Link>
                                        ))}
                                        {dayItems.length > 3 && (
                                            <div className="text-[10px] text-slate-500">+{dayItems.length - 3} more</div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {!loading && view === "week" && (
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead className="bg-slate-50">
                                <tr>
                                    <th className="text-left px-3 py-2 font-medium">Employee</th>
                                    {visibleDays.map((d) => (
                                        <th key={d.toISOString()} className="text-center px-2 py-2 font-medium text-[10px]">
                                            {d.toLocaleDateString("en-IN", { weekday: "short" })}<br />
                                            <span className="font-mono">{d.getDate()}/{d.getMonth() + 1}</span>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(items.reduce((acc, req) => {
                                    if (!acc[req.employee_id]) acc[req.employee_id] = { emp: req, days: {} };
                                    eachDayInRange(new Date(req.from_date), new Date(req.to_date)).forEach((d) => {
                                        const k = isoDate(d.getFullYear(), d.getMonth(), d.getDate());
                                        acc[req.employee_id].days[k] = req;
                                    });
                                    return acc;
                                }, {})).map(([eid, { emp, days }]) => (
                                    <tr key={eid} className="border-t border-slate-100" data-testid={`tc-week-row-${eid}`}>
                                        <td className="px-3 py-2">
                                            <div className="font-medium text-slate-800">{emp.employee_name || emp.employee_code}</div>
                                            <div className="text-[10px] text-slate-500">{emp.department || "—"}</div>
                                        </td>
                                        {visibleDays.map((d) => {
                                            const k = isoDate(d.getFullYear(), d.getMonth(), d.getDate());
                                            const req = days[k];
                                            return (
                                                <td key={k} className="px-1 py-1 text-center align-middle">
                                                    {req ? (
                                                        <Link to={`/leave/requests/${req.id}`}
                                                            className={`block px-1 py-0.5 rounded text-[10px] border ${LEAVE_COLORS[req.leave_type_code] || LEAVE_COLORS.DEFAULT}`}>
                                                            {req.leave_type_code}
                                                        </Link>
                                                    ) : <span className="text-slate-200">·</span>}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {items.length === 0 && <div className="text-center py-8 text-slate-400">No leaves in this week.</div>}
                    </div>
                )}
            </section>

            <div className="mt-3 text-xs text-slate-500" data-testid="tc-count">
                {items.length} leave entries in this window
            </div>
        </div>
    );
}
